import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCategoryTree, type CategoryNode } from '@/api/category';
import { getProductPages, isOutsideFlag, resolveSpecImage, type ProductSpecItem } from '@/api/product';
import ProductCard from './components/ProductCard';

const ALL_CATEGORY_ID = 'all';
const PAGE_SIZE = 20;

type SortKey = 'sales' | 'new' | 'priceAsc' | 'priceDesc';
type ProductCardData = ReturnType<typeof mapProductCard>;

const SORT_API_MAP: Record<SortKey, 'sales' | 'new' | 'price_asc' | 'price_desc'> = {
  sales: 'sales',
  new: 'new',
  priceAsc: 'price_asc',
  priceDesc: 'price_desc',
};

function toId(id: string | number) {
  return String(id);
}

function findCategoryName(nodes: CategoryNode[], targetId: string): string {
  for (const node of nodes) {
    if (toId(node.id) === targetId) return node.title;
    if (node.children?.length) {
      const found = findCategoryName(node.children, targetId);
      if (found) return found;
    }
  }
  return '';
}

function mapProductCard(item: ProductSpecItem) {
  const price = Number(item.price ?? 0);
  const stock = typeof item.stock === 'number' ? item.stock : Number(item.stock ?? 0);
  return {
    /** 详情页按 goods_id 拉取 */
    id: String(item.goods_id || item.goods?.id || item.id),
    specId: String(item.id),
    name: item.goods?.title || '未命名物资',
    code: item.goods?.code || '',
    price: Number.isFinite(price) ? price : 0,
    unit: item.unit || '件',
    model: item.name || '',
    stock: Number.isFinite(stock) ? stock : 0,
    supplier: item.supplier?.name || '',
    image: resolveSpecImage(item.pic, item.goods?.m_pic),
    isOutside: isOutsideFlag(item.is_outside),
  };
}

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || ALL_CATEGORY_ID;
  const activeSubCategory = searchParams.get('subcategory') || '';
  const searchQuery = searchParams.get('search') || '';
  const supplierQuery = searchParams.get('supplier') || '';
  const hasKeywordSearch = Boolean(searchQuery || supplierQuery);

  const [sortBy, setSortBy] = useState<SortKey>('sales');
  const [onlyOutside, setOnlyOutside] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categoryError, setCategoryError] = useState('');

  const [products, setProducts] = useState<ProductCardData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [productLoading, setProductLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [productError, setProductError] = useState('');

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadingLockRef = useRef(false);
  const filterGenRef = useRef(0);
  const fetchRef = useRef<
    ((pageNum: number, mode: 'replace' | 'append', gen: number) => Promise<void>) | undefined
  >(undefined);

  const hasMore = products.length < total;

  const updateParams = (mutate: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    next.delete('page');
    setSearchParams(next);
  };

  const fetchProducts = useCallback(
    async (pageNum: number, mode: 'replace' | 'append', gen: number) => {
      if (mode === 'append' && loadingLockRef.current) return;
      loadingLockRef.current = true;

      if (mode === 'replace') setProductLoading(true);
      else setLoadingMore(true);
      setProductError('');

      try {
        const categoryId = activeSubCategory || (activeCategory !== ALL_CATEGORY_ID ? activeCategory : 0);
        const res = await getProductPages({
          page: pageNum,
          pagenumber: PAGE_SIZE,
          search: searchQuery.trim() || undefined,
          supplier_name: supplierQuery.trim() || undefined,
          category_id: categoryId || 0,
          sortBy: SORT_API_MAP[sortBy],
          ...(onlyOutside ? { is_outside: true } : {}),
        });

        if (gen !== filterGenRef.current) return;

        const ok = res.error === false || Number(res.error) === 0;
        if (!ok) {
          setProductError(res.message || res.msg || '物资加载失败');
          if (mode === 'replace') {
            setProducts([]);
            setTotal(0);
          }
          return;
        }

        const list = Array.isArray(res.data) ? res.data.map(mapProductCard) : [];
        setTotal(Number(res.total ?? 0) || 0);
        setProducts((prev) => (mode === 'replace' ? list : [...prev, ...list]));
      } catch {
        if (gen !== filterGenRef.current) return;
        setProductError('物资加载失败，请稍后重试');
        if (mode === 'replace') {
          setProducts([]);
          setTotal(0);
        }
      } finally {
        if (gen === filterGenRef.current) {
          loadingLockRef.current = false;
          setProductLoading(false);
          setLoadingMore(false);
        } else {
          loadingLockRef.current = false;
        }
      }
    },
    [activeCategory, activeSubCategory, searchQuery, supplierQuery, sortBy, onlyOutside],
  );

  fetchRef.current = fetchProducts;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCategoryLoading(true);
      setCategoryError('');
      try {
        const res = await getCategoryTree();
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        if (!ok) {
          setCategoryError(res.message || res.msg || '分类加载失败');
          setCategoryTree([]);
          return;
        }
        setCategoryTree(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) {
          setCategoryError('分类加载失败，请稍后重试');
          setCategoryTree([]);
        }
      } finally {
        if (!cancelled) setCategoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 筛选变化：重置并加载第一页
  useEffect(() => {
    const gen = ++filterGenRef.current;
    loadingLockRef.current = false;
    setPage(1);
    setProducts([]);
    setTotal(0);
    void fetchProducts(1, 'replace', gen);
  }, [fetchProducts]);

  // 滚动加载下一页
  useEffect(() => {
    if (page <= 1) return;
    void fetchRef.current?.(page, 'append', filterGenRef.current);
  }, [page]);

  const loadNextPage = useCallback(() => {
    if (loadingLockRef.current || productLoading || loadingMore || !hasMore || productError) return;
    setPage((p) => p + 1);
  }, [productLoading, loadingMore, hasMore, productError]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadNextPage();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loadNextPage, products.length, hasMore]);

  const toggleExpand = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId],
    );
  };

  const handleCategoryChange = (categoryId: string) => {
    updateParams((params) => {
      params.delete('subcategory');
      if (categoryId === ALL_CATEGORY_ID) params.delete('category');
      else params.set('category', categoryId);
    });
  };

  const handleSubCategoryChange = (subCategoryId: string) => {
    updateParams((params) => {
      const parent = categoryTree.find((c) =>
        (c.children || []).some((child) => toId(child.id) === subCategoryId),
      );
      if (parent) params.set('category', toId(parent.id));
      params.set('subcategory', subCategoryId);
    });
  };

  const clearSubCategory = () => {
    updateParams((params) => {
      params.delete('subcategory');
    });
  };

  const clearSearch = () => {
    updateParams((params) => {
      params.delete('search');
      params.delete('supplier');
    });
  };

  const handleSortChange = (next: SortKey) => {
    setSortBy(next);
  };

  const titleText = activeSubCategory
    ? findCategoryName(categoryTree, activeSubCategory) || '全部物资'
    : activeCategory === ALL_CATEGORY_ID
      ? '全部物资'
      : findCategoryName(categoryTree, activeCategory) || '全部物资';

  return (
    <div className="flex flex-1">
      <aside className="w-56 xl:w-60 shrink-0 border-r border-background-200 bg-white">
        <div className="p-4 sticky top-0">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-menu-fold-line text-primary-500" />
            物资分类
          </h3>
          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => handleCategoryChange(ALL_CATEGORY_ID)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-md text-sm transition-colors ${
                activeCategory === ALL_CATEGORY_ID && !activeSubCategory
                  ? 'bg-primary-50 text-primary-700 font-semibold border-l-[3px] border-primary-500'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-apps-line" />
                </span>
                全部物资
              </span>
            </button>

            {categoryLoading && (
              <p className="px-3 py-4 text-xs text-foreground-400">分类加载中...</p>
            )}

            {!categoryLoading && categoryError && (
              <p className="px-3 py-4 text-xs text-red-500">{categoryError}</p>
            )}

            {!categoryLoading &&
              !categoryError &&
              categoryTree.map((cat) => {
                const catId = toId(cat.id);
                const children = cat.children || [];
                const hasChildren = children.length > 0;
                const expanded = expandedCategories.includes(catId);

                return (
                  <div key={catId}>
                    <button
                      type="button"
                      onClick={() => {
                        handleCategoryChange(catId);
                        if (hasChildren && !expanded) toggleExpand(catId);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-2.5 rounded-md text-sm transition-colors ${
                        activeCategory === catId && !activeSubCategory
                          ? 'bg-primary-50 text-primary-700 font-semibold border-l-[3px] border-primary-500'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i className="ri-folder-line" />
                        </span>
                        <span className="truncate">{cat.title}</span>
                      </span>
                      {hasChildren ? (
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label={expanded ? '收起分类' : '展开分类'}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(catId);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleExpand(catId);
                            }
                          }}
                          className="w-5 h-5 flex items-center justify-center rounded hover:bg-background-200/50 shrink-0"
                        >
                          <i
                            className={`ri-arrow-down-s-line transition-transform text-xs ${
                              expanded ? 'rotate-180' : ''
                            }`}
                          />
                        </span>
                      ) : (
                        <span className="opacity-50 shrink-0 ml-1 text-xs">
                          ({cat.specs_count ?? 0})
                        </span>
                      )}
                    </button>

                    {hasChildren && expanded && (
                      <div className="ml-7 space-y-0.5 mt-0.5">
                        {children.map((sub) => {
                          const subId = toId(sub.id);
                          return (
                            <button
                              type="button"
                              key={subId}
                              onClick={() => handleSubCategoryChange(subId)}
                              className={`flex items-center justify-between w-full px-3 py-1.5 rounded text-xs transition-colors ${
                                activeSubCategory === subId
                                  ? 'bg-primary-50 text-primary-700 font-medium border-l-[3px] border-primary-500'
                                  : 'text-foreground-600 hover:bg-background-100'
                              }`}
                            >
                              <span className="truncate">{sub.title}</span>
                              <span className="opacity-50 shrink-0 ml-1">
                                ({sub.specs_count ?? 0})
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 bg-background-50">
        <div className="bg-white border-b border-background-200 sticky top-0 z-30 px-4 md:px-6 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 shrink-0">
              <h3 className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{titleText}</h3>
              <span className="text-xs text-foreground-500 whitespace-nowrap">
                {hasKeywordSearch ? '找到' : '共'}{' '}
                <strong className="text-foreground-900">{total}</strong> 件物资
                {products.length > 0 && products.length < total && (
                  <span className="ml-1">（已加载 {products.length}）</span>
                )}
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap justify-end">
              <button
                type="button"
                onClick={() => setOnlyOutside((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors whitespace-nowrap ${
                  onlyOutside
                    ? 'bg-orange-50 text-orange-700 border-orange-400'
                    : 'border-background-300 text-foreground-600 hover:border-orange-400 hover:text-orange-600'
                }`}
              >
                <span
                  className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    onlyOutside
                      ? 'bg-orange-500 border-orange-500'
                      : 'border-background-300 bg-white'
                  }`}
                >
                  {onlyOutside && <i className="ri-check-line text-[10px] text-white" />}
                </span>
                清单外
              </button>
              {(
                [
                  ['sales', '销量'],
                  ['new', '新品'],
                  ['priceAsc', '价格'],
                  ['priceDesc', '价格'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSortChange(key)}
                  className={`px-3 py-1.5 rounded-md text-xs border transition-colors whitespace-nowrap ${
                    sortBy === key
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  {label}
                  {key === 'priceAsc' && <i className="ri-arrow-up-line ml-1" />}
                  {key === 'priceDesc' && <i className="ri-arrow-down-line ml-1" />}
                </button>
              ))}

              {hasKeywordSearch && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="px-3 py-1.5 rounded-md text-xs border border-primary-300 text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-close-line mr-0.5" />
                  清除{supplierQuery ? '供应商' : ''} &quot;{searchQuery || supplierQuery}&quot;
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 pb-4">
          {productLoading && products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 mb-3" />
              <p className="text-sm text-foreground-500">物资加载中...</p>
            </div>
          ) : productError && products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <p className="text-sm text-red-500 mb-2">{productError}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mb-4">
                <i className="ri-search-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm text-foreground-500 mb-2">暂无符合条件的物资</p>
              {hasKeywordSearch && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  清除搜索条件
                </button>
              )}
              {onlyOutside && (
                <button
                  type="button"
                  onClick={() => setOnlyOutside(false)}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-1"
                >
                  清除清单外筛选
                </button>
              )}
              {activeSubCategory && (
                <button
                  type="button"
                  onClick={clearSubCategory}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-1"
                >
                  清除二级分类筛选
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-4">
                {products.map((product) => (
                  <ProductCard
                    key={product.specId || product.id}
                    id={product.id}
                    specId={product.specId}
                    name={product.name}
                    code={product.code}
                    price={product.price}
                    unit={product.unit}
                    model={product.model}
                    stock={product.stock}
                    supplier={product.supplier}
                    image={product.image}
                    isOutside={product.isOutside}
                  />
                ))}
              </div>

              <div ref={loadMoreRef} className="py-6 flex flex-col items-center justify-center gap-2">
                {loadingMore && (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
                    <p className="text-xs text-foreground-400">加载更多...</p>
                  </>
                )}
                {!loadingMore && hasMore && (
                  <p className="text-xs text-foreground-400">继续下滑加载更多</p>
                )}
                {!loadingMore && !hasMore && products.length > 0 && (
                  <p className="text-xs text-foreground-400">已加载全部 {total} 件物资</p>
                )}
                {productError && products.length > 0 && (
                  <button
                    type="button"
                    onClick={loadNextPage}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    加载失败，点击重试
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
